// Döviz Kurları Servisi - Backend Proxy Kullanır
const runtimeOrigin = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : 'http://localhost:5000';
let base = process.env.REACT_APP_API_URL || runtimeOrigin;
base = base.replace(/\/$/, '');
// Base URL '/api' ile bitmiyorsa ekle
const API_BASE_URL = base.endsWith('/api') ? base : `${base}/api`;

// Silent logging helpers (toggle with DEBUG)
const DEBUG = false;
const dlog = (...args) => { if (DEBUG) console.log(...args); };
const derr = (...args) => { if (DEBUG) console.error(...args); };

class CurrencyService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 10 * 60 * 1000; // 10 dakika (mobil için uzatıldı)
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  async fetchCurrencyRates() {
    const cacheKey = 'currency_rates';
    const cachedData = this.cache.get(cacheKey);
    
    // Cache kontrolü
    if (cachedData && (Date.now() - cachedData.timestamp) < this.cacheTimeout) {
      dlog('💰 KURU: Cache\'den alındı');
      return cachedData.data;
    }

    return this._fetchWithRetry();
  }

  async _fetchWithRetry() {
    const cacheKey = 'currency_rates';
    const cachedData = this.cache.get(cacheKey);
    
    try {
      dlog(`💰 KURU: API isteği başlatılıyor (deneme ${this.retryCount + 1}/${this.maxRetries + 1})...`);
      
      // Mobil cihazlar için timeout artırıldı
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 saniye

      const response = await fetch(`${API_BASE_URL}/currency/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Döviz kurları alınamadı');
      }
      
      // Başarılı istek sonrası retry count'u sıfırla
      this.retryCount = 0;
      
      // Cache'e kaydet
      this.cache.set(cacheKey, {
        data: result.data,
        timestamp: Date.now()
      });
      
      // LocalStorage'a da backup kaydet
      this._saveToLocalStorage(result.data);
      
      dlog('✅ KURU: API\'den başarıyla alındı');
      return result.data;
      
    } catch (error) {
      derr(`❌ KURU: API hatası (deneme ${this.retryCount + 1}):`, error.message);
      
      // Mobil cihazlarda network hataları için retry logic
      if (this.retryCount < this.maxRetries && 
          (error.name === 'AbortError' || 
           error.message.includes('Failed to fetch') || 
           error.message.includes('Network request failed') ||
           error.message.includes('ERR_NETWORK'))) {
        
        this.retryCount++;
        const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 10000); // Exponential backoff, max 10s
        dlog(`🔄 KURU: ${delay}ms sonra yeniden deneniyor...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this._fetchWithRetry();
      }
      
      // Retry limiti aşıldı veya farklı hata, cache'den eski veri döndür
      this.retryCount = 0;
      
      if (cachedData) {
        dlog('📦 KURU: Cache\'den eski veri döndürülüyor');
        return cachedData.data;
      }

      // Cache yoksa localStorage backup'ını dene
      const backupData = this._loadFromLocalStorage();
      if (backupData) {
        dlog('💾 KURU: LocalStorage backup\'ından veri döndürülüyor');
        return backupData;
      }
      
      throw error;
    }
  }



  // LocalStorage'a backup kaydet (mobil offline durumları için)
  _saveToLocalStorage(data) {
    try {
      localStorage.setItem('jansoft_currency_backup', JSON.stringify({
        data: data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('LocalStorage backup kaydedilemedi:', error);
    }
  }

  // LocalStorage'dan backup yükle
  _loadFromLocalStorage() {
    try {
      const backup = localStorage.getItem('jansoft_currency_backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        // 24 saat geçerli backup
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.warn('LocalStorage backup yüklenemedi:', error);
    }
    return null;
  }

  // Cache temizle
  clearCache() {
    this.cache.clear();
    localStorage.removeItem('jansoft_currency_backup');
  }

  // Cache durumunu kontrol et
  getCacheStatus() {
    const cacheKey = 'currency_rates';
    const cachedData = this.cache.get(cacheKey);
    
    if (!cachedData) {
      return { cached: false };
    }
    
    const age = Date.now() - cachedData.timestamp;
    const isExpired = age >= this.cacheTimeout;
    
    return {
      cached: true,
      age: age,
      expired: isExpired,
      expiresIn: this.cacheTimeout - age
    };
  }
}

// Singleton instance
const currencyService = new CurrencyService();

export default currencyService;
